// frontend/src/ts/protocols/persistence/useUploadStatusStore.ts
import {create} from 'zustand'
import {getUploadStore, UploadStoreNotAvailableError} from './uploadStore'
import type {PendingUpload, UploadSummary, DataCollectionStatus} from './uploadTypes'
import {IProtocolRawFile} from "@/protocols/types/IProtocolRawFile.ts";

const POLL_INTERVAL_MS = 1000

export interface DeadUpload {
    id: number
    filename: string
    state: string
    error: string | null
    createdAt: number
    attemptCount: number
}

export interface BrowserUploadContext {
    companyId: string
    protocolId: string
    subjectId: string
    protocolStepId?: string
}

export interface BrowserUploadProgress {
    tempId: number
    filename: string
    fileSize: number
    progress: number          // 0..1 from the XHR onUploadProgress
    status: 'in_flight' | 'persisted' | 'dead' | 'cancelled'
    error?: string
    startedAt: number
    bytesWritten: number
    abort?: AbortController
    /** Retained on failure so the upload can be retried (auto-retry on
     * reconnect or manual Retry) without asking the user to re-pick the
     * file. Only present while the page is alive — browser uploads have
     * no durable queue. */
    file?: File
    /** The args the upload was started with; needed by retry. */
    context?: BrowserUploadContext
    /** Total attempts (initial + retries). Capped by the retry logic. */
    attemptCount: number
}

interface UploadStatusState {
    pendingUploads: PendingUpload[]
    summary: UploadSummary
    dataCollectionStatus: DataCollectionStatus | null
    deadUploads: DeadUpload[]
    bootstrapped: boolean
    hydrationError: string | null
    pollTimer: ReturnType<typeof setInterval> | null
    browserUploads: Map<number, BrowserUploadProgress>
    serverFiles: IProtocolRawFile[]
    /**
     * Confirmed server-side file count -- files that have actually
     * finished uploading, as last observed by the polling refresh
     * (`setServerFiles` / `setServerFileCount`). Deliberately excludes
     * anything still queued or uploading; see `summary` /
     * `browserUploads` for in-progress state.
     */
    serverFileCount: number

    bootstrap: (companyId: string, protocolId: string) => Promise<void>
    shutdown: () => void
    refresh: (companyId: string, protocolId: string) => Promise<void>
    setServerFiles: (files: IProtocolRawFile[]) => void
    setServerFileCount: (count: number) => void

    startBrowserUpload(
        filename: string,
        fileSize: number,
        abort?: AbortController,
        file?: File,
        context?: BrowserUploadContext,
    ): number

    updateBrowserUploadProgress(tempId: number, bytesWritten: number, progress: number): void

    completeBrowserUpload(tempId: number): void

    failBrowserUpload(tempId: number, error: string): void

    cancelBrowserUpload(tempId: number): void

    /** Reset a dead entry to in_flight for a retry attempt (bumps
     * attemptCount). The retry itself is driven by the caller. */
    restartBrowserUpload(tempId: number, abort?: AbortController): void

    /** Remove an entry entirely (dismiss a failed upload / cleanup). */
    removeBrowserUpload(tempId: number): void
}

function pendingUploadToDeadUpload(p: PendingUpload): DeadUpload {
    return {
        id: p.id,
        filename: p.filename,
        state: p.state,
        error: p.lastError,
        createdAt: p.createdAt,
        attemptCount: p.attemptCount,
    }
}

/**
 * Map browser-mode upload entries into the durable queue row shape so
 * PendingUploadTable can render browser uploads similarly to the Rust
 * queue. In-flight entries are surfaced as `uploading`; failed entries
 * remain `dead` for retry. Completed/cancelled entries are omitted.
 */
function browserUploadRows(browserUploads: Map<number, BrowserUploadProgress>): PendingUpload[] {
    const rows: PendingUpload[] = []
    browserUploads.forEach((entry) => {
        if (entry.status !== 'in_flight' && entry.status !== 'dead') return
        rows.push({
            id: entry.tempId,
            state: entry.status === 'in_flight' ? 'uploading' : 'dead',
            foundPath: '',
            path: null,
            filename: entry.filename,
            fileSize: entry.fileSize,
            uploadProgress: entry.status === 'in_flight' ? entry.progress : 0,
            remoteFileId: null,
            attemptCount: entry.attemptCount,
            lastAttemptAt: entry.startedAt,
            lastError: entry.status === 'dead' ? (entry.error ?? null) : null,
            createdAt: entry.startedAt,
            protocolStepId: entry.context?.protocolStepId,
        })
    })
    return rows
}

function emptySummary(): UploadSummary {
    return {found: 0, copied: 0, pending: 0, uploading: 0, persisted: 0, dead: 0}
}

export const useUploadStatusStore = create<UploadStatusState>((set, get) => ({
    pendingUploads: [],
    summary: emptySummary(),
    dataCollectionStatus: null,
    deadUploads: [],
    bootstrapped: false,
    hydrationError: null,
    pollTimer: null,
    browserUploads: new Map(),
    serverFiles: [],
    serverFileCount: 0,

    bootstrap: async (companyId, protocolId) => {
        // Always re-bootstrap when called (re-mount, protocol switch). We
        // do NOT short-circuit on `bootstrapped` — that was the cause of the
        // "UI doesn't recognize watching files after re-mount" regression:
        // shutdown() resets `bootstrapped` to false, but the next mount's
        // bootstrap call would still see the same `pollTimer` handle (non-null),
        // skip the `setInterval`, and the polling would never restart with
        // the new (companyId, protocolId). Instead, always tear down the
        // existing timer and start fresh with the new args.
        const prevTimer = get().pollTimer
        if (prevTimer !== null) clearInterval(prevTimer)
        set({pollTimer: null})

        const store = getUploadStore()
        let hydrationError: string | null = null
        let summary: UploadSummary = emptySummary()
        let pendingRows: PendingUpload[] = []
        let status: DataCollectionStatus | null = null
        try {
            summary = await store.summary(companyId, protocolId)
        } catch (e) {
            hydrationError = e instanceof Error ? e.message : String(e)
        }
        try {
            pendingRows = await store.getPendingUploads({
                companyId, protocolId,
                stateFilter: ['found', 'copied', 'pending', 'uploading'],
            })
        } catch (e) {
            if (hydrationError === null) {
                hydrationError = e instanceof Error ? e.message : String(e)
            }
        }
        try {
            status = await store.getDataCollectionStatus(companyId, protocolId)
        } catch {
            // status is non-critical; fall through with null
        }

        const allRows = [...pendingRows, ...browserUploadRows(get().browserUploads)]
        const deadUploads: DeadUpload[] = allRows
            .filter((r) => r.state === 'dead')
            .map(pendingUploadToDeadUpload)

        set({
            summary, pendingUploads: allRows,
            dataCollectionStatus: status, deadUploads, hydrationError,
        })

        const timer = setInterval(() => {
            // Pull CURRENT companyId/protocolId from the store so the polling
            // follows subsequent navigations instead of being permanently
            // locked on the args captured at bootstrap time. (The Rust
            // singletons ignore companyId/protocolId anyway and return the
            // global state, but keeping the args current lets future
            // per-protocol helpers plug in here.)
            const s = get()
            if (!s.bootstrapped) return
            void s.refresh(companyId, protocolId).catch((e) => {
                if (!(e instanceof UploadStoreNotAvailableError)) {
                    console.error('[useUploadStatusStore] refresh failed', e)
                }
            })
        }, POLL_INTERVAL_MS)
        set({pollTimer: timer, bootstrapped: true})
    },

    shutdown: () => {
        const timer = get().pollTimer
        if (timer !== null) clearInterval(timer)
        set({
            pendingUploads: [],
            summary: emptySummary(),
            dataCollectionStatus: null,
            deadUploads: [],
            bootstrapped: false,
            hydrationError: null,
            pollTimer: null,
            browserUploads: new Map(),
            serverFiles: [],
            serverFileCount: 0,
        })
    },

    refresh: async (companyId, protocolId) => {
        const store = getUploadStore()
        const [summary, pendingRows, status] = await Promise.all([
            store.summary(companyId, protocolId).catch((e) => {
                console.error('[useUploadStatusStore] summary failed', e)
                return emptySummary()
            }),
            store.getPendingUploads({
                companyId, protocolId,
                stateFilter: ['found', 'copied', 'pending', 'uploading', 'dead'],
            }).catch((e) => {
                console.error('[useUploadStatusStore] getPendingUploads failed', e)
                return [] as PendingUpload[]
            }),
            store.getDataCollectionStatus(companyId, protocolId).catch(() => null),
        ])
        const allRows = [...pendingRows, ...browserUploadRows(get().browserUploads)]
        const deadUploads: DeadUpload[] = allRows
            .filter((r) => r.state === 'dead')
            .map(pendingUploadToDeadUpload)
        set({summary, pendingUploads: allRows, dataCollectionStatus: status, deadUploads})
    },

    setServerFiles: (files: IProtocolRawFile[]) => {
        set({serverFiles: files, serverFileCount: files.length})
    },
    setServerFileCount: (count: number) => {
        set({serverFileCount: count})
    },

    startBrowserUpload: (filename, fileSize, abort, file, context) => {
        const tempId = Date.now() + Math.floor(Math.random() * 1000)
        set((s) => {
            const next = new Map(s.browserUploads)
            next.set(tempId, {
                tempId, filename, fileSize,
                progress: 0, status: 'in_flight',
                startedAt: Date.now(),
                bytesWritten: 0,
                abort,
                file,
                context,
                attemptCount: 1,
            })
            return {browserUploads: next}
        })
        return tempId
    },

    updateBrowserUploadProgress: (tempId, bytesWritten, progress) => {
        set((s) => {
            const existing = s.browserUploads.get(tempId)
            if (!existing) return {}
            const next = new Map(s.browserUploads)
            next.set(tempId, {...existing, progress, bytesWritten})
            return {browserUploads: next}
        })
    },

    completeBrowserUpload: (tempId) => {
        set((s) => {
            if (!s.browserUploads.has(tempId)) return {}
            const next = new Map(s.browserUploads)
            next.delete(tempId)
            // The file itself isn't counted as server-confirmed until the
            // next poll's setServerFiles/setServerFileCount call reflects
            // it -- this just stops the entry from showing as in-flight.
            return {browserUploads: next}
        })
    },

    failBrowserUpload: (tempId, error) => {
        set((s) => {
            const existing = s.browserUploads.get(tempId)
            if (!existing) return {}
            const next = new Map(s.browserUploads)
            next.set(tempId, {...existing, status: 'dead', error, progress: 0})
            return {browserUploads: next}
        })
    },

    cancelBrowserUpload: (tempId) => {
        set((s) => {
            const existing = s.browserUploads.get(tempId)
            if (!existing) return {}
            // Trigger the underlying AbortController; the catch path in the
            // coordinator will mark the row as 'cancelled' on the next refresh.
            existing.abort?.abort()
            const next = new Map(s.browserUploads)
            next.set(tempId, {...existing, status: 'cancelled'})
            return {browserUploads: next}
        })
    },

    restartBrowserUpload: (tempId, abort) => {
        set((s) => {
            const existing = s.browserUploads.get(tempId)
            if (!existing) return {}
            const next = new Map(s.browserUploads)
            next.set(tempId, {
                ...existing,
                status: 'in_flight',
                progress: 0,
                bytesWritten: 0,
                error: undefined,
                abort,
                attemptCount: existing.attemptCount + 1,
            })
            return {browserUploads: next}
        })
    },

    removeBrowserUpload: (tempId) => {
        set((s) => {
            if (!s.browserUploads.has(tempId)) return {}
            const next = new Map(s.browserUploads)
            next.delete(tempId)
            return {browserUploads: next}
        })
    },
}))
